// 위력 계산 섹션 - 효과의 method, in_stat, out_stat 설정
import React, { useEffect, useState } from 'react';
import { Form, Select, Row, Col, Checkbox, Typography, Alert, Space, InputNumber } from 'antd';
import { FormInstance } from 'antd/es/form';

const { Option } = Select;
const { Text } = Typography;

interface PowerCalculationSectionProps {
  form: FormInstance;
  stats?: any[];
  effectTypes?: any[];
}

// 영웅 DB에서 사용하는 operator 타입들
const OPERATOR_TYPES = [
  { code: 'Value', name: '고정값', description: '지정된 값을 그대로 사용' },
  { code: '×', name: '배수', description: '입력 스탯에 배수를 적용' },
  { code: '%+', name: '백분율 증가', description: '기존 값에 백분율로 더함' },
  { code: '%-', name: '백분율 감소', description: '기존 값에서 백분율로 뺌' },
  { code: '☆+', name: '스탯 기반 증가', description: '입력 스탯 기반으로 증가' },
  { code: '☆-', name: '스탯 기반 감소', description: '입력 스탯 기반으로 감소' },
  { code: 'replace', name: '대체', description: '기존 값을 완전히 대체' },
];

const PowerCalculationSection: React.FC<PowerCalculationSectionProps> = ({ form, stats = [], effectTypes = [] }) => {
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [isFromAffected, setIsFromAffected] = useState(false);

  // Form 값 감시
  const operatorValue = Form.useWatch('operator', form);
  const isFromAffectedValue = Form.useWatch('is_from_affected', form);
  const catValue = Form.useWatch('cat', form);


  useEffect(() => {
    if (operatorValue) {
      setSelectedMethod(operatorValue);
    }
  }, [operatorValue]);

  useEffect(() => {
    setIsFromAffected(Boolean(isFromAffectedValue));
  }, [isFromAffectedValue]);

  // 사용 가능한 스탯 목록 가져오기
  const getAvailableStats = () => {
    // props로 전달받은 스탯 사용
    return stats;
  };

  // 효과 타입에 따라 필요한 필드 결정
  const shouldShowStatFields = () => {
    const selectedType = effectTypes.find(type => type.effect_code === catValue);
    if (!selectedType) return true; // 효과 타입을 찾을 수 없으면 기본값 true
    
    const effectName = selectedType.effect_name;
    // 스탯 계산이 필요 없는 효과들 (즉시 효과 중 피해/치유는 필요)
    const noStatNeeded = 
      (effectName.includes('[즉시]') && !effectName.includes('피해') && !effectName.includes('치유')) ||
      effectName.includes('이동불가') || effectName.includes('모든행동불가') ||
      effectName.includes('물리행동불가') || effectName.includes('마법행동불가') ||
      effectName.includes('어그로') || effectName.includes('해제') || 
      effectName.includes('차단') || effectName.includes('이동') ||
      effectName.includes('소환') || effectName.includes('페이즈') || 
      effectName.includes('오브젝트') || effectName.includes('진화');
    
    return !noStatNeeded;
  };

  // operator에 따라 in_stat 필요 여부 결정
  const needsInStat = () => {
    // Replace는 in_stat 활성화
    if (selectedMethod === 'Replace') return true;
    return selectedMethod && ['×', '%+', '%-', '☆+', '☆-'].includes(selectedMethod);
  };

  // cat에 따라 out_stat 필요 여부 결정  
  const needsOutStat = () => {
    // cat에서 해당 효과 타입 찾기
    const selectedType = effectTypes.find(type => type.effect_code === catValue);
    if (!selectedType) return true; // 효과 타입을 찾을 수 없으면 기본값 true
    
    const effectName = selectedType.effect_name;
    // DOT, HOT, [즉시]피해, [즉시]치유는 out_stat이 필수가 아님
    const noOutStatRequired = 
      effectName.includes('DOT') || 
      effectName.includes('HOT') || 
      (effectName.includes('[즉시]') && (effectName.includes('피해') || effectName.includes('치유')));
    
    const result = !noOutStatRequired;
    return result;
  };

  // operator가 Value일 때 고정값 입력 필요 여부
  const needsFixedValue = () => {
    return selectedMethod === 'Value';
  };

  if (!shouldShowStatFields()) {
    return null;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text strong>효과 계산 설정</Text>
      
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item 
            label="계산 기준" 
            name="is_from_affected" 
            valuePropName="checked"
            tooltip="체크하면 시전자가 아닌 대상의 스탯을 기준으로 계산합니다"
          >
            <Checkbox>대상 기준 계산 (is_from_affected)</Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item 
            label="계산 방식 (operator)" 
            name="operator"
            tooltip="효과의 계산 방식을 선택합니다"
          >
            <Select placeholder="계산 방식 선택" allowClear>
              {OPERATOR_TYPES.map(operator => (
                <Option key={operator.code} value={operator.code}>
                  {operator.name} ({operator.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item 
            label="입력 스탯 (in_stat)" 
            name="in_stat"
            rules={[{ 
              required: !!needsInStat(), 
              message: '선택한 계산 방식에는 입력 스탯이 필요합니다' 
            }]}
          >
            <Select 
              placeholder="계산에 사용할 스탯"
              showSearch
              allowClear
              disabled={!needsInStat()}
              filterOption={(input, option) =>
                option?.children ? String(option.children).toLowerCase().includes(input.toLowerCase()) : false
              }
            >
              {getAvailableStats().map(stat => (
                <Option key={stat.stat_code} value={stat.stat_code}>
                  {stat.stat_name} ({stat.stat_code})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item noStyle dependencies={['cat']}>
            {() => (
              <Form.Item 
                label="출력 스탯 (out_stat)" 
                name="out_stat"
                rules={[{ 
                  required: needsOutStat(), 
                  message: '이 효과 타입은 출력 스탯이 필요합니다' 
                }]}
              >
                <Select 
                  placeholder="영향을 줄 스탯"
                  showSearch
                  allowClear
                  disabled={!needsOutStat()}
                  filterOption={(input, option) =>
                    option?.children ? String(option.children).toLowerCase().includes(input.toLowerCase()) : false
                  }
                >
                  {getAvailableStats().map(stat => (
                    <Option key={stat.stat_code} value={stat.stat_code}>
                      {stat.stat_name} ({stat.stat_code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>
        </Col>
      </Row>

      {/* 고정값 입력 필드 */}
      {needsFixedValue() && (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              label="고정값" 
              name="fixed_value"
              rules={[{ required: true, message: '고정값을 입력해주세요' }]}
              tooltip="직접 적용할 고정값"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="고정값 입력"
                min={0}
                step={0.1}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {selectedMethod && (
        <Alert
          message="계산 방식 설명"
          description={OPERATOR_TYPES.find(m => m.code === selectedMethod)?.description}
          type="info"
        />
      )}

      {/* 효과 타입별 추가 안내 */}
      {catValue === 'StatReinforcement' && (
        <Alert
          message="스탯 강화 효과"
          description="대상의 특정 스탯을 강화합니다. method와 out_stat을 설정해주세요."
          type="success"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'StatWeaken' && (
        <Alert
          message="스탯 약화 효과"
          description="대상의 특정 스탯을 약화시킵니다. method와 out_stat을 설정해주세요."
          type="warning"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'TickDamage' && (
        <Alert
          message="틱당 데미지"
          description="주기적으로 피해를 입힙니다. tick_period_s로 주기를 설정하세요."
          type="error"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'TickHealing' && (
        <Alert
          message="틱당 회복"
          description="주기적으로 치유합니다. tick_period_s로 주기를 설정하세요."
          type="success"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'ShieldSpecial' && (
        <Alert
          message="특수 보호막"
          description="특수 조건이 있는 보호막입니다. in_stat으로 보호막 양을 계산할 수 있습니다."
          type="info"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'BossEnrage' && (
        <Alert
          message="광역 강화"
          description="보스가 광역적으로 강화됩니다. BossEnrage 스탯을 사용할 수 있습니다."
          type="error"
          style={{ marginTop: 8 }}
        />
      )}

      {catValue === 'GroundEffect' && (
        <Alert
          message="장판 생성"
          description="지면에 지속적인 효과를 생성합니다. tick_period_s로 피해 주기를 설정하세요."
          type="warning"
          style={{ marginTop: 8 }}
        />
      )}
    </Space>
  );
};

export default PowerCalculationSection;